import { getManageDependentsForm } from 'api/operations/formService';
import { autorun, makeAutoObservable } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import SubscriberDependentDelegate from 'store/modules/elections/dependents/subDependentDelegate';
import { useEffect, useState } from 'react';
import { PersonInfo } from 'types/electionService';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types/dynamicForms';
import { DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import SubscriberDelegate from '../subscriberDelegate';

interface DomainData {
  dependentDelegate: SubscriberDependentDelegate;
  subscriberDelegate: SubscriberDelegate;
  formId: string;
}

export class UpdateDependentModalStore {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  private readonly _dependentDelegate: SubscriberDependentDelegate;
  private readonly _subscriberDelegate: SubscriberDelegate;
  private readonly _formId: string;
  private _updateDependentModal = new ModalDelegate();

  public dynamicJSONFormDelegate = new DynamicFormDelegate({
    currentUserRoles: this._rootStore.userStore.userRoles,
    fetchForm: async (formId: string): Promise<FormObjectFromJSON> => {
      return getManageDependentsForm(
        formId,
        {
          common: this._subscriberDelegate.subscriber.common,
          custom: {
            ...this._subscriberDelegate.subscriber.custom,
            dependents: [this._dependentDelegate.dependent],
          },
        },
        this._dependentDelegate.dependent.id,
        this._rootStore.userStore.sponsorId,
      );
    },
    onSubmit: this.updateDependent.bind(this),
  });

  public get showUpdateDependentModal(): boolean {
    return this._updateDependentModal.isVisible;
  }

  public async openUpdateDependentModal(): Promise<void> {
    this._updateDependentModal.open();
    await this.dynamicJSONFormDelegate.buildForm(this._formId, { ...this._dependentDelegate.dependent });
  }

  public closeUpdateDependentModal(): void {
    this.dynamicJSONFormDelegate.resetState();
    this._updateDependentModal.close();
  }

  public async updateDependent(): Promise<void> {
    if (!this.dynamicJSONFormDelegate.formController) return;

    const updatedDependentInfo = {
      ...this.dynamicJSONFormDelegate.formAnswers,
      id: this._dependentDelegate.dependent.id,
    } as unknown as PersonInfo;

    await this._subscriberDelegate.updateDependent(updatedDependentInfo);
    this.closeUpdateDependentModal();
  }

  constructor({ dependentDelegate, subscriberDelegate, formId }: DomainData) {
    this._dependentDelegate = dependentDelegate;
    this._subscriberDelegate = subscriberDelegate;
    this._formId = formId;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

export function useUpdateDependentModalStore({
  dependentDelegate,
  subscriberDelegate,
  formId,
}: DomainData): UpdateDependentModalStore | null {
  const [store, setStore] = useState<UpdateDependentModalStore | null>(
    () => new UpdateDependentModalStore({ dependentDelegate, subscriberDelegate, formId }),
  );

  useEffect(() => {
    autorun(() => {
      setStore(new UpdateDependentModalStore({ dependentDelegate, subscriberDelegate, formId }));
    });

    return (): void => {
      setStore(null);
    };
  }, [dependentDelegate, subscriberDelegate, formId]);
  return store;
}
